import { useCallback, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, apiGet } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { IT_MONTHS, IT_DOW_LETTER, ymd, dowMon0, dayHeader, relativeLabel, fmtDuration, groupByDay } from "@/lib/agenda";
import { todayISO } from "@/lib/format";
import { useResource } from "@/pwa/useResource";
import { useOnlineGuard } from "@/pwa/useOnlineGuard";
import { StatusBadge } from "@/components/StatusBadge";
import { EditSessionDialog } from "@/components/dialogs/EditSessionDialog";
import { CompleteDialog } from "@/components/dialogs/CompleteDialog";
import { DataUnavailable } from "@/components/pwa/OfflineBanner";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ChevronLeft, ChevronRight, CheckCircle2, XCircle, Pencil, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";

export default function Agenda() {
  const nav = useNavigate();
  const { guard } = useOnlineGuard();
  const now = todayISO();
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [selected, setSelected] = useState(null);
  const [complete, setComplete] = useState(null);
  const [editSess, setEditSess] = useState(null);
  const dayRefs = useRef({});

  const { data, unavailable, reload } = useResource(
    useCallback(async () => { const r = await apiGet("/sessions", { params: { status: "Pianificata" } }); return { data: r.data, stale: r.stale }; }, []),
    []
  );
  const sessions = data || [];

  const monthPrefix = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}`;
  const isCurrentMonth = monthPrefix === now.slice(0, 7);

  // Grid indicators reflect all planned sessions in the displayed month.
  const countByDay = useMemo(() => {
    const map = {};
    for (const s of sessions) {
      const key = String(s.date).slice(0, 10);
      if (key.startsWith(monthPrefix)) map[key] = (map[key] || 0) + 1;
    }
    return map;
  }, [sessions, monthPrefix]);

  // List scope: a specific selected day, or "Tutti i prossimi" (from today forward).
  const scopeSessions = useMemo(() => {
    if (selected) return sessions.filter((s) => String(s.date).slice(0, 10) === selected);
    return sessions.filter((s) => String(s.date).slice(0, 10) >= now);
  }, [sessions, selected, now]);
  const grouped = useMemo(() => groupByDay(scopeSessions), [scopeSessions]);

  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const firstDow = dowMon0(ymd(cursor.y, cursor.m, 1));
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const goPrev = () => setCursor((c) => (c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 }));
  const goNext = () => setCursor((c) => (c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 }));
  const goToday = () => { const d = new Date(); setCursor({ y: d.getFullYear(), m: d.getMonth() }); setSelected(null); };

  const onDayTap = (dayNum) => {
    const key = ymd(cursor.y, cursor.m, dayNum);
    setSelected((cur) => (cur === key ? null : key));
  };

  const cancel = async (s) => {
    try { await api.patch(`/sessions/${s.id}/status`, { status: "Annullata" }); toast.success("Prestazione annullata"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Agenda</h1>
        <Button variant="outline" size="sm" data-testid="agenda-today" onClick={goToday} className="rounded-full">Oggi</Button>
      </div>

      {/* Month header */}
      <div className="flex items-center justify-between">
        <button data-testid="agenda-prev-month" onClick={goPrev} aria-label="Mese precedente" className="grid place-items-center h-10 w-10 rounded-full border border-border bg-card hover:bg-secondary"><ChevronLeft className="h-5 w-5" /></button>
        <div data-testid="agenda-month-label" className="font-head text-lg font-bold">{IT_MONTHS[cursor.m]} {cursor.y}</div>
        <button data-testid="agenda-next-month" onClick={goNext} aria-label="Mese successivo" className="grid place-items-center h-10 w-10 rounded-full border border-border bg-card hover:bg-secondary"><ChevronRight className="h-5 w-5" /></button>
      </div>

      {/* Month grid */}
      <div className="rounded-2xl border border-border bg-card p-3" data-testid="agenda-grid">
        <div className="grid grid-cols-7 gap-1 mb-1">
          {IT_DOW_LETTER.map((d, i) => <div key={i} className="text-center text-xs font-semibold text-muted-foreground py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((day, i) => {
            if (day === null) return <div key={`e${i}`} />;
            const key = ymd(cursor.y, cursor.m, day);
            const n = countByDay[key] || 0;
            const isToday = key === now;
            const isSel = key === selected;
            return (
              <button key={key} data-testid={`agenda-day-${key}`} onClick={() => onDayTap(day)}
                className={`relative aspect-square rounded-xl flex items-center justify-center text-sm transition-colors
                  ${isSel ? "bg-primary text-primary-foreground" : isToday ? "bg-secondary font-bold" : "hover:bg-secondary"}`}>
                <span>{day}</span>
                {n > 0 && <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 text-[9px] font-bold leading-none ${isSel ? "text-primary-foreground" : "text-primary"}`}>•{n}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Scope control — single authoritative view */}
      <div className="flex items-center justify-between gap-2 flex-wrap" data-testid="agenda-scope">
        <span className="text-sm font-semibold">{selected ? `Giorno · ${fmtDate(selected)}` : "Tutti i prossimi"}</span>
        {selected && <Button variant="outline" size="sm" data-testid="agenda-scope-reset" onClick={() => setSelected(null)} className="rounded-full">Tutti i prossimi</Button>}
      </div>

      {/* Agenda list — exactly one of: content OR a single empty state */}
      {unavailable ? (
        <DataUnavailable />
      ) : scopeSessions.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="agenda-empty">Nessun appuntamento pianificato.</p>
      ) : (
        <div className="space-y-5" data-testid="agenda-list">
          {[...grouped.entries()].map(([dateKey, items]) => {
            const rel = relativeLabel(dateKey);
            return (
              <div key={dateKey} ref={(el) => (dayRefs.current[dateKey] = el)} data-testid={`agenda-section-${dateKey}`} className="scroll-mt-20">
                <div className="text-xs font-bold tracking-wide text-muted-foreground mb-2">
                  {dayHeader(dateKey)}{rel ? ` · ${rel.toUpperCase()}` : ""}
                </div>
                <div className="space-y-2">
                  {items.map((s) => (
                    <div key={s.id} data-testid={`agenda-appt-${s.id}`} className="rounded-2xl border border-border bg-card p-4">
                      <div className="flex items-start gap-3 flex-wrap">
                        <div className="font-head font-extrabold tabular text-primary shrink-0">{s.start_time || "—"}</div>
                        <button onClick={() => s.dog_ids?.[0] && nav(`/cani/${s.dog_ids[0]}`)} className="flex-1 min-w-0 text-left">
                          <div className="font-semibold break-words">{(s.dog_names || []).join(", ") || "—"}</div>
                          <div className="text-sm text-muted-foreground break-words">{s.owner_name}</div>
                          <div className="text-sm break-words">{s.service_type}</div>
                          <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap mt-0.5">
                            {s.duration_minutes ? <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDuration(s.duration_minutes)}</span> : null}
                            {s.location ? <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{s.location}</span> : null}
                          </div>
                        </button>
                        {s.overdue && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#FFF3E9] text-[#C2410C] border border-[#F5C6A5]">Da verificare</span>}
                      </div>
                      <div className="mt-3 flex gap-2 flex-wrap">
                        <Button size="sm" data-testid={`agenda-complete-${s.id}`} onClick={guard(() => setComplete(s))} className="rounded-full"><CheckCircle2 className="h-4 w-4 mr-1" />Svolta</Button>
                        <Button size="sm" variant="outline" data-testid={`agenda-edit-${s.id}`} onClick={guard(() => setEditSess(s))} className="rounded-full"><Pencil className="h-4 w-4 mr-1" />Modifica</Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="outline" data-testid={`agenda-cancel-${s.id}`} onClick={guard(() => {})} className="rounded-full"><XCircle className="h-4 w-4 mr-1" />Annulla</Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Annullare questa prestazione?</AlertDialogTitle>
                              <AlertDialogDescription asChild>
                                <div>
                                  <span className="block">Cane: <b>{(s.dog_names || []).join(", ") || "—"}</b></span>
                                  <span className="block">Servizio: <b>{s.service_type}</b></span>
                                  <span className="block">Data: <b>{fmtDate(s.date)}{s.start_time ? ` · ${s.start_time}` : ""}</b></span>
                                  <span className="block mt-2">La prestazione resterà nello storico con stato <b>Annullata</b> e non consumerà crediti del pacchetto.</span>
                                </div>
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Indietro</AlertDialogCancel>
                              <AlertDialogAction data-testid={`agenda-cancel-confirm-${s.id}`} onClick={() => cancel(s)}>Conferma annullamento</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <EditSessionDialog open={!!editSess} onOpenChange={(v) => !v && setEditSess(null)} session={editSess} onSaved={reload} />
      <CompleteDialog open={!!complete} onOpenChange={(v) => !v && setComplete(null)} session={complete} onDone={reload} />
    </div>
  );
}
